import { Box, Divider, Stack, Typography } from '@mui/material';
import React from 'react';

interface Props {
  title: string;
  description?: string;
  /** Optional right-aligned element in the header (e.g. an action button). */
  action?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * The content pane of a settings sub-page: a titled header with an optional
 * description/action, a divider, then the section body.
 *
 * Intentionally borderless/transparent — it sits inside the unified settings
 * panel (see SettingsLayout), which provides the surface and outline.
 */
export const SettingsSection = ({ title, description, action, children }: Props): React.JSX.Element => (
  <Box sx={{ width: '100%' }}>
    <Stack
      direction={'row'}
      alignItems={'flex-start'}
      justifyContent={'space-between'}
      spacing={2}
      sx={{ px: 3, py: 2 }}
    >
      <Stack direction={'column'} spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography variant={'h6'} fontWeight={700}>
          {title}
        </Typography>
        {description && (
          <Typography variant={'body2'} color={'text.secondary'}>
            {description}
          </Typography>
        )}
      </Stack>
      {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
    </Stack>
    <Divider />
    <Box sx={{ px: 3, py: 3 }}>{children}</Box>
  </Box>
);
